import { Component, ChangeDetectionStrategy, input, output, signal, computed, inject, ElementRef } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Avatar } from '../avatar/avatar';
import { UserPreferencesPanel, ProfileUser } from '../user-preferences-panel/user-preferences-panel.component';
import type { SupportedLanguage } from '../services/language.service';

/** Position for the fixed dropdown menu */
interface MenuPosition {
  top: string | null;
  bottom: string | null;
  left: string | null;
  right: string | null;
}

/**
 * Sidebar profile component that displays user info at the bottom of the sidebar.
 */
@Component({
  selector: 'zag-sidebar-profile',
  imports: [TranslatePipe, Avatar, UserPreferencesPanel],
  template: `
    <div class="sidebar-profile" [class.sidebar-profile--collapsed]="collapsed()" [class.sidebar-profile--open]="menuOpen()">
      <button
        type="button"
        class="sidebar-profile__trigger"
        (click)="toggleMenu()"
        [attr.aria-expanded]="menuOpen()"
        aria-haspopup="menu"
        [attr.aria-label]="'navigation.userMenu' | translate"
      >
        <div class="sidebar-profile__avatar-wrap">
          <zag-avatar [name]="user().name" [src]="user().avatarUrl" size="md" />
          @if (unreadCount() > 0) {
            <span class="sidebar-profile__unread-badge" [attr.aria-label]="unreadCount() + ' unread notifications'">{{
              unreadCount() > 99 ? '99+' : unreadCount()
            }}</span>
          }
        </div>

        @if (!collapsed()) {
          <span class="sidebar-profile__name">{{ user().name }}</span>
        }
      </button>

      @if (menuOpen()) {
        <div
          class="sidebar-profile__menu"
          [class.sidebar-profile__menu--collapsed]="collapsed()"
          [style.top]="menuPosition().top"
          [style.bottom]="menuPosition().bottom"
          [style.left]="menuPosition().left"
          [style.right]="menuPosition().right"
          role="menu"
        >
          <zag-user-preferences-panel
            [user]="user()"
            [showHeader]="true"
            [profileRoute]="profileRoute()"
            [showNotifications]="showNotifications()"
            [unreadCount]="unreadCount()"
            (logout)="onSignOutClick()"
            (linkClick)="closeMenu()"
            (versionInfoClick)="onVersionInfoClick()"
          />
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        flex-shrink: 0;
      }

      .sidebar-profile {
        position: relative;
      }

      .sidebar-profile__trigger {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
        min-width: 0;
        height: 100%;
        padding: var(--spacing-md);
        background: transparent;
        border: none;
        color: var(--text-color-primary);
        cursor: pointer;
        text-align: left;
        overflow: hidden;
        transition: background-color var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: -2px;
        }
      }

      .sidebar-profile__avatar-wrap {
        position: relative;
        flex-shrink: 0;
      }

      .sidebar-profile__unread-badge {
        position: absolute;
        top: -12px;
        right: -12px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        box-sizing: content-box;
        background: var(--color-error, #ef5350);
        color: var(--color-neutral-0, #fff);
        font-size: 10px;
        font-weight: 700;
        line-height: 16px;
        text-align: center;
        border-radius: 8px;
        border: 2px solid var(--sidebar-bg-color, var(--surface-color-default));
        pointer-events: none;
      }

      .sidebar-profile__name {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: var(--font-size-caption);
        font-weight: 500;
        text-align: left;
      }

      .sidebar-profile__menu {
        position: fixed;
        width: 298px;
        background: var(--background-color-elevated);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        box-shadow: var(--shadow-lg);
        z-index: 9999;
        overflow: hidden;
      }

      /* Mobile: Full width with rounded top corners */
      @media (max-width: 539px) {
        .sidebar-profile__menu {
          width: 100%;
          max-width: 100%;
          border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
          border-bottom: none;
        }
      }

      /* Collapsed state adjustments */
      .sidebar-profile--collapsed {
        .sidebar-profile__trigger {
          justify-content: center;
        }
      }

      /* Tablet and smaller: Always collapsed (max 1279px) */
      @media (max-width: 1279px) {
        .sidebar-profile__trigger {
          justify-content: center;
        }

        .sidebar-profile__name {
          display: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-sidebar-profile',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class SidebarProfileComponent {
  private readonly elementRef = inject(ElementRef);

  /** User data to display */
  readonly user = input.required<ProfileUser>();

  /** Whether sidebar is collapsed */
  readonly collapsed = input<boolean>(false);

  /** Current language */
  readonly currentLanguage = input.required<SupportedLanguage>();

  /** Current theme */
  readonly currentTheme = input.required<'light' | 'dark'>();

  /** Available languages */
  readonly supportedLanguages: SupportedLanguage[] = ['en', 'de', 'fr', 'it'];

  /** Emits when sign out is clicked */
  readonly signOut = output<void>();

  /** Emits when "My Profile" is clicked */
  readonly navigateToProfile = output<void>();

  /** Emits when the app status is clicked */
  readonly versionInfoClick = output<void>();

  /** Route to the profile page */
  readonly profileRoute = input<string | null>('/profile');

  /** Whether to show notifications */
  readonly showNotifications = input<boolean>(true);

  /** Number of unread notifications — drives the badge on the avatar trigger */
  readonly unreadCount = input<number>(0);

  protected readonly menuOpen = signal(false);
  private readonly triggerRect = signal<DOMRect | null>(null);

  /** Calculate menu position based on trigger element and collapsed state */
  protected readonly menuPosition = computed<MenuPosition>(() => {
    const rect = this.triggerRect();
    const isCollapsed = this.collapsed() || this.isEffectivelyCollapsed();
    const isMobile = this.isMobile();

    if (!rect) {
      return { top: null, bottom: null, left: null, right: null };
    }

    // Mobile mode: position above the trigger, full width
    if (isMobile) {
      return {
        top: null,
        bottom: `${window.innerHeight - rect.top}px`, // 0 gap above trigger
        left: '0',
        right: '0',
      };
    }

    if (isCollapsed) {
      // Collapsed mode: position to the right of the trigger, aligned at bottom
      return {
        top: null,
        bottom: `${window.innerHeight - rect.bottom}px`,
        left: `${rect.right + 4}px`, // 4px gap (--spacing-xs)
        right: null,
      };
    } else {
      // Expanded mode: position above the trigger, aligned at left
      return {
        top: null,
        bottom: `${window.innerHeight - rect.top + 4}px`, // 4px gap above trigger
        left: `${rect.left}px`,
        right: null,
      };
    }
  });

  protected onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen.set(false);
    }
  }

  protected onEscape(): void {
    this.menuOpen.set(false);
  }

  protected toggleMenu(): void {
    // Update trigger rect before toggling menu
    const trigger = this.elementRef.nativeElement.querySelector('.sidebar-profile__trigger');
    if (trigger) {
      this.triggerRect.set(trigger.getBoundingClientRect());
    }
    this.menuOpen.update(open => !open);
  }

  /** Check if sidebar is effectively collapsed (via media query) */
  private isEffectivelyCollapsed(): boolean {
    return globalThis.matchMedia('(max-width: 1279px)').matches;
  }

  /** Check if viewport is mobile (max-width: 539px) */
  private isMobile(): boolean {
    return globalThis.matchMedia('(max-width: 539px)').matches;
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected onVersionInfoClick(): void {
    this.closeMenu();
    this.versionInfoClick.emit();
  }

  protected onSignOutClick(): void {
    this.menuOpen.set(false);
    this.signOut.emit();
  }

  protected onNavigateToProfile(): void {
    this.menuOpen.set(false);
    this.navigateToProfile.emit();
  }
}
